import React, { useEffect, useRef } from 'react';
import { Viewport } from '../WorldCanvas/WorldCanvas';
import { Zone } from '../../types/zone';

interface ZoneOverlayProps {
  zones    : Zone[];
  viewport : Viewport;
  width    : number;
  height   : number;
  myUserId?: number;
}

/**
 * Renders reserved zones as semi-transparent rectangles over the world canvas.
 *
 * Screen ↔ world mapping (Y-up, identical to useWorldRenderer):
 *   screen_x =  (world_x − vp.x) × zoom + w/2
 *   screen_y = −(world_y − vp.y) × zoom + h/2
 *
 * Each zone is drawn as a filled + stroked rectangle with a name label.
 * The owner's own zones are tinted green; foreign zones are tinted amber.
 */
export const ZoneOverlay: React.FC<ZoneOverlayProps> = ({
  zones, viewport, width, height, myUserId,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, width, height);
    if (zones.length === 0) return;

    const { x: vpX, y: vpY, zoom } = viewport;

    const toScreenX = (wx: number) =>  (wx - vpX) * zoom + width  / 2;
    const toScreenY = (wy: number) => -(wy - vpY) * zoom + height / 2;

    for (const zone of zones) {
      const isOwn = myUserId != null && zone.user_id === myUserId;

      // World coords of the zone rectangle corners.
      // x_min/x_max map directly; y_max appears at top on screen (Y-up).
      const sx = toScreenX(zone.x_min);
      const sy = toScreenY(zone.y_max);      // top of rect on screen
      const sw = (zone.x_max - zone.x_min + 1) * zoom;
      const sh = (zone.y_max - zone.y_min + 1) * zoom;

      // Skip zones entirely outside the viewport.
      if (sx + sw < 0 || sx > width || sy + sh < 0 || sy > height) continue;

      // Fill
      ctx.fillStyle = isOwn
        ? 'rgba(80, 220, 120, 0.12)'
        : 'rgba(255, 190, 50, 0.12)';
      ctx.fillRect(sx, sy, sw, sh);

      // Border
      ctx.strokeStyle = isOwn
        ? 'rgba(80, 220, 120, 0.55)'
        : 'rgba(255, 190, 50, 0.55)';
      ctx.lineWidth = Math.max(1, zoom * 0.08);
      ctx.strokeRect(sx, sy, sw, sh);

      // Name label — shown whenever the zone is at least a few pixels on screen.
      // Clipped to the zone rect so it never bleeds outside.
      if (sw > 8 && sh > 8) {
        const fontSize = Math.max(9, Math.min(13, zoom * 1.2));
        ctx.font         = `${fontSize}px 'Courier New', monospace`;
        ctx.textBaseline = 'top';
        ctx.textAlign    = 'left';
        const label = zone.name || `#${zone.id}`;
        const pad   = Math.min(4, sw * 0.08);

        ctx.save();
        ctx.beginPath();
        ctx.rect(sx, sy, sw, sh);
        ctx.clip();

        ctx.fillStyle = 'rgba(0,0,0,0.65)';
        ctx.fillText(label, sx + pad + 1, sy + pad + 1);
        ctx.fillStyle = isOwn ? 'rgba(120,255,160,0.95)' : 'rgba(255,210,80,0.95)';
        ctx.fillText(label, sx + pad, sy + pad);

        ctx.restore();
      }
    }
  }, [zones, viewport, width, height, myUserId]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{
        position     : 'absolute',
        top          : 0,
        left         : 0,
        pointerEvents: 'none',
        zIndex       : 4,
      }}
    />
  );
};
